import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { CreateInventoryItemDto } from '@/api/services';
import { useCreateInventoryItem } from '@/api/hooks';
import { useAuth } from '@/context/AuthContext';
import { Separator } from '@/components/ui/separator';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const categories = [
  'Drilling',
  'Safety',
  'Tools',
  'Parts',
  'Equipment',
  'Consumables',
  'Chemicals',
];
const units = ['EA', 'FT', 'M', 'KG', 'LB', 'GAL', 'L', 'BOX', 'SET'];

interface InventoryFormData {
  sku: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  unit: string;
  minStockLevel: string;
  maxStockLevel: string;
  reorderPoint: string;
  leadTimeDays: string;
  unitCost: string;
  isActive: boolean;
}

const emptyFormData: InventoryFormData = {
  sku: '',
  name: '',
  description: '',
  category: 'Parts',
  subcategory: '',
  unit: 'EA',
  minStockLevel: '10',
  maxStockLevel: '',
  reorderPoint: '',
  leadTimeDays: '',
  unitCost: '',
  isActive: true,
};

export default function InventoryNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<InventoryFormData>(emptyFormData);

  const createMutation = useCreateInventoryItem();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sku || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload: CreateInventoryItemDto = {
      sku: formData.sku,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category,
      subcategory: formData.subcategory || undefined,
      unit: formData.unit || 'EA',
      companyId: user?.companyId || '',
      minStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : undefined,
      maxStockLevel: formData.maxStockLevel ? parseInt(formData.maxStockLevel) : undefined,
      reorderPoint: formData.reorderPoint ? parseInt(formData.reorderPoint) : undefined,
      leadTimeDays: formData.leadTimeDays ? parseInt(formData.leadTimeDays) : undefined,
      unitCost: formData.unitCost ? parseFloat(formData.unitCost) : undefined,
      isActive: formData.isActive,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Item created successfully');
        navigate('/inventory');
      },
    });
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Inventory', href: '/inventory' },
          { label: 'New item' },
        ]}
        title="Add Inventory Item"
        description="Details required to register a stock item against a category and unit of measure."
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Basic Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku">SKU *</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                  placeholder="DRL-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Drill Bit 8.5in"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter item description..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory</Label>
                <Input
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="e.g., PDC Bits"
                />
              </div>
            </div>
          </div>

          {/* Stock Levels */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Stock Levels</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minStockLevel">Min Stock Level</Label>
                <Input
                  id="minStockLevel"
                  type="number"
                  value={formData.minStockLevel}
                  onChange={(e) => setFormData({ ...formData, minStockLevel: e.target.value })}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxStockLevel">Max Stock Level</Label>
                <Input
                  id="maxStockLevel"
                  type="number"
                  value={formData.maxStockLevel}
                  onChange={(e) => setFormData({ ...formData, maxStockLevel: e.target.value })}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reorderPoint">Reorder Point</Label>
                <Input
                  id="reorderPoint"
                  type="number"
                  value={formData.reorderPoint}
                  onChange={(e) => setFormData({ ...formData, reorderPoint: e.target.value })}
                  placeholder="20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leadTimeDays">Lead Time (days)</Label>
                <Input
                  id="leadTimeDays"
                  type="number"
                  value={formData.leadTimeDays}
                  onChange={(e) => setFormData({ ...formData, leadTimeDays: e.target.value })}
                  placeholder="7"
                />
              </div>
            </div>
          </div>

          {/* Cost */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Cost</h3>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="unitCost">Unit Cost ($)</Label>
              <Input
                id="unitCost"
                type="number"
                step="0.01"
                value={formData.unitCost}
                onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Status */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Status</h3>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.isActive ? 'Item is active' : 'Item is inactive'}
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>

          {/* Actions — sticky so save stays reachable: the form runs well past
              one viewport, and the button sat at the very bottom of it. */}
          <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-3 border-t bg-background/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <Button type="button" variant="outline" onClick={() => navigate('/inventory')}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Item'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
